import { useCallback, useMemo } from "react";

import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { v4 } from "uuid";

import { getTicketsPurchasedIndex } from "@/canvas/features/FormV2/helpers/getTicketsPurchasedIndex";
import { getInitialGroupMemberCounts } from "@/canvas/features/FormV2/helpers/getInitialGroupMemberCounts";
import { useUserInputContext } from "@/common/contexts/UserInputContext";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput, RateOutput } from "@/common/types/trpc";

function generateDefaultQuestionAnswer(question: Pick<QuestionOutput, "id" | "type">) {
  return {
    id: v4(),
    questionId: question.id,
    type: question.type,
    choiceIds: [],
    answer: "",
    answers: [""],
  };
}

export type UseTicketingV2CardStateOptions = {
  form: UseFormReturn<TicketingPaymentInput>;
  participants: UseFieldArrayReturn<TicketingPaymentInput, "customAnswersOfParticipants">;
  rates: RateOutput[];
  allRates: RateOutput[];
  questions: QuestionOutput[];
  animations?: {
    rotate?: () => void;
    reverseRotate?: () => void;
    triggerHearts?: () => void;
  };
  callbacks?: {
    onItemAmountChanged: ({ from, to, rate }: { from: number; to: number; rate: RateOutput }) => void;
  };
};

export function useTicketingV2CardState({
  participants,
  form,
  rates,
  allRates,
  questions,
  animations,
  callbacks,
}: UseTicketingV2CardStateOptions) {
  const { setValue } = form;
  const { addUserInput } = useUserInputContext();

  const {
    fields: participantsFields,
    insert: insertCustomAnswersOfParticipants,
    remove: removeCustomAnswersOfParticipants,
  } = participants;

  const rateIdToParticipantCount = useMemo(() => {
    return rates.reduce((acc: Record<string, number>, r) => {
      acc[r.id] = participantsFields.filter((p) => p.rateId === r.id).length;
      return acc;
    }, {});
  }, [participantsFields, rates]);

  const generateOnIncrementAmount = useCallback(
    ({
      rateId,
      title,
      remainingMemberCapacity = Infinity,
    }: {
      rateId: string;
      title: string;
      remainingMemberCapacity?: number;
    }) => {
      return (): boolean => {
        const currentRateIndex = getTicketsPurchasedIndex(allRates, rateId);
        const currentRate = allRates[currentRateIndex];

        if (!currentRate) {
          // The rate no longer exists
          return false;
        }

        const purchasedNumber = form.getValues(`ticketsPurchased.${currentRateIndex}.purchasedNumber`) || 0;
        const minimumToBuy = currentRate.minimumToBuy || 1;
        const attendeesByTicket = currentRate.attendeesByTicket || 1;
        const numberOfTicketsToAdd = purchasedNumber === 0 ? minimumToBuy : 1;
        const memberCounts = currentRate.groupMembershipMaxMembers
          ? getInitialGroupMemberCounts(numberOfTicketsToAdd, remainingMemberCapacity)
          : Array.from({ length: numberOfTicketsToAdd }, () => attendeesByTicket);
        if (memberCounts.length === 0) {
          return false;
        }

        animations?.rotate?.();
        animations?.triggerHearts?.();

        const questionsForRate = questions.filter(
          (q) => q.hasRate && q.rateQuestions?.some((rq) => rq.rateId === rateId)
        );

        const newPurchasedNumber = purchasedNumber + numberOfTicketsToAdd;
        setValue(`ticketsPurchased.${currentRateIndex}.purchasedNumber`, newPurchasedNumber);

        addUserInput({
          [title]: newPurchasedNumber.toString(),
        });

        // Find the last index of the current rate
        const lastIndexOfRate = participantsFields
          .map((field, idx) => ({ field, idx }))
          .reverse()
          .find(({ field }) => field.rateId === rateId)?.idx;

        let insertionIndex: number;

        if (lastIndexOfRate !== undefined) {
          // Insert after the last existing participant of the current rate
          insertionIndex = lastIndexOfRate + 1;
        } else {
          // If no participants exist for this rate, calculate insertion index based on rate order
          insertionIndex = allRates.slice(0, currentRateIndex).reduce((acc, r) => {
            return acc + (rateIdToParticipantCount[r.id] || 0);
          }, 0);
        }

        if (insertionIndex === 0 && participantsFields.length > 0) {
          setValue("customAnswersOfParticipants.0.isForBuyer", false);
        }

        const newCustomAnswersOfParticipants = memberCounts.flatMap((memberCount, groupIndex) =>
          Array.from({ length: memberCount }, (_, memberIndex) => ({
            rateId,
            answers: questionsForRate.map(generateDefaultQuestionAnswer),
            ...(currentRate.groupMembershipMaxMembers
              ? {
                  membershipGroupIndex: purchasedNumber + groupIndex,
                  isForBuyer: insertionIndex === 0 && groupIndex === 0 && memberIndex === 0,
                }
              : {}),
          }))
        );

        // Insert new participants sequentially
        newCustomAnswersOfParticipants.forEach((customAnswers, idx) => {
          insertCustomAnswersOfParticipants(insertionIndex + idx, customAnswers, { shouldFocus: false });
        });

        callbacks?.onItemAmountChanged?.({
          from: purchasedNumber,
          to: newPurchasedNumber,
          rate: currentRate,
        });

        return true;
      };
    },
    [
      allRates,
      form,
      animations,
      questions,
      setValue,
      addUserInput,
      participantsFields,
      callbacks,
      rateIdToParticipantCount,
      insertCustomAnswersOfParticipants,
    ]
  );

  const generateOnDecrementAmount = useCallback(
    ({ rateId, title }: { rateId: string; title: string }) => {
      return () => {
        const currentRateIndex = getTicketsPurchasedIndex(allRates, rateId);
        const currentRate = allRates[currentRateIndex];

        if (!currentRate) {
          // The rate no longer exists
          return;
        }

        animations?.reverseRotate?.();

        const purchasedNumber = form.getValues(`ticketsPurchased.${currentRateIndex}.purchasedNumber`) || 0;
        const minimumToBuy = currentRate.minimumToBuy || 1;
        const attendeesByTicket = currentRate.attendeesByTicket || 1;

        // Determine the new purchased number: allow going directly to 0 or decrement if above minimumToBuy
        const newPurchasedNumber = purchasedNumber > minimumToBuy ? purchasedNumber - 1 : 0;

        setValue(`ticketsPurchased.${currentRateIndex}.purchasedNumber`, newPurchasedNumber);
        addUserInput({
          [title]: newPurchasedNumber.toString(),
        });

        // Gather indices of participants associated with the current rate
        const participantsForRateIndices = participantsFields
          .map((field, idx) => ({ field, idx }))
          .filter(({ field }) => field.rateId === currentRate.id)
          .map(({ idx }) => idx);

        if (participantsForRateIndices.length === 0) {
          return;
        }

        // Calculate the total number of participants to remove
        const numberOfParticipantsToRemove =
          newPurchasedNumber === 0 ? participantsForRateIndices.length : attendeesByTicket;

        // Get the indices to remove based on the calculated number
        const indicesToRemove = currentRate.groupMembershipMaxMembers
          ? participantsForRateIndices.filter(
              (index) => (participantsFields[index].membershipGroupIndex ?? 0) >= newPurchasedNumber
            )
          : participantsForRateIndices.slice(-numberOfParticipantsToRemove);

        // Sort indices in descending order to prevent index shifting during removal
        indicesToRemove.sort((a, b) => b - a);

        // Remove participants sequentially
        indicesToRemove.forEach((index) => {
          removeCustomAnswersOfParticipants(index);
        });

        callbacks?.onItemAmountChanged?.({
          from: purchasedNumber,
          to: newPurchasedNumber,
          rate: currentRate,
        });
      };
    },
    [
      addUserInput,
      allRates,
      animations,
      callbacks,
      form,
      participantsFields,
      removeCustomAnswersOfParticipants,
      setValue,
    ]
  );

  return {
    allParticipants: participantsFields,
    generateOnIncrementAmount,
    generateOnDecrementAmount,
  };
}
